import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';

interface LanguageSwitcherProps {
  className?: string;
}

interface Language {
  code: string;
  name: string;
  shortCode: string;
  flag: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ className = '' }) => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const languages: Language[] = [
    { code: 'en', name: 'English', shortCode: 'EN', flag: '🇺🇸' },
    { code: 'zh', name: '简体中文', shortCode: 'CN', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', shortCode: 'JP', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', shortCode: 'KR', flag: '🇰🇷' },
    { code: 'fr', name: 'Français', shortCode: 'FR', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', shortCode: 'DE', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', shortCode: 'PT', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский', shortCode: 'RU', flag: '🇷🇺' },
    { code: 'ar', name: 'العربية', shortCode: 'AR', flag: '🇸🇦' }
  ];

  const currentLang = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleLanguageChange = (langCode: string) => {
    // 获取当前路径的部分（去掉语言前缀）
    const currentPath = location.pathname.split('/').slice(2).join('/');
    // 构建新的URL，英文版本使用根路径
    const newPath = langCode === 'en' 
      ? (currentPath ? `/${currentPath}/` : '/') 
      : `/${langCode}${currentPath ? `/${currentPath}` : ''}/`;
    
    i18n.changeLanguage(langCode);
    navigate(newPath);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors ${className}`}
      >
        <span className="mr-1">{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.name}</span>
        <span className="sm:hidden text-xs">{currentLang.shortCode}</span>
        <ChevronDownIcon className="w-4 h-4 ml-1" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 mt-2 py-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`${className} w-full flex items-center px-4 py-2 hover:bg-gray-50 transition-colors`}
              >
                <span className="mr-2">{lang.flag}</span>
                <span className="hidden sm:inline">{lang.name}</span>
                <span className="sm:hidden">
                  <span className="mr-2">{lang.shortCode}</span>
                  <span className="text-gray-500 text-xs">{lang.name}</span>
                </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};